import * as THREE from 'three';
import { ViewOrientationTargetKind } from '../camera/view-orientation/standardViewTypes';

/**
 * 视图定向材质工厂。
 *
 * 设计说明：
 * 1. 统一管理主面、连接面、角点面的基础材质创建逻辑，避免三处重复维护同一套参数；
 * 2. 这里仍然返回新的材质实例，不能做全局单例，否则 hover 与 active 改色会互相污染；
 * 3. 该工厂只负责基础材质配置，不参与几何尺寸和交互状态计算。
 */
export class ViewOrientationMaterialFactory {
  /** 视图定向边线默认颜色（供外部读取用于状态重置）。 */
  public static readonly DEFAULT_OUTLINE_COLOR = '#2f2f2f';

  /** 视图定向边线透明度。 */
  private static readonly VIEW_ORIENTATION_OUTLINE_OPACITY = 0.92;

  /** 视图定向网格默认透明度。 */
  private static readonly OPACITY = 0.6;

  /** 视图定向网格默认主面颜色。 */
  private static readonly FACE_COLOR = '#bcbcbc';

  /** 视图定向网格默认边面颜色。 */
  private static readonly EDGE_COLOR = '#bcbcbc';

  /** 视图定向网格默认角面颜色。 */
  private static readonly CORNER_COLOR = '#bcbcbc';

  /**
   * 创建视图定向基础材质。
   *
   * @param kind 命中目标类型。
   * @returns 视图定向基础材质。
   */
  public static createBasicMaterial(kind: ViewOrientationTargetKind): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
      color: ViewOrientationMaterialFactory.resolveElementColor(kind),
      transparent: ViewOrientationMaterialFactory.OPACITY < 1,
      opacity: ViewOrientationMaterialFactory.OPACITY,
      side: THREE.DoubleSide,
      depthWrite: ViewOrientationMaterialFactory.OPACITY >= 1,
    });
  }

  /**
   * 创建视图定向边线几何。
   *
   * @param geometry 面片几何。
   * @returns 边线几何。
   */
  public static createOutlineGeometry(geometry: THREE.BufferGeometry): THREE.EdgesGeometry {
    return new THREE.EdgesGeometry(geometry);
  }

  /**
   * 创建视图定向边线材质。
   *
   * @returns 边线材质。
   */
  public static createOutlineMaterial(): THREE.LineBasicMaterial {
    return new THREE.LineBasicMaterial({
      color: ViewOrientationMaterialFactory.DEFAULT_OUTLINE_COLOR,
      transparent: true,
      opacity: ViewOrientationMaterialFactory.VIEW_ORIENTATION_OUTLINE_OPACITY,
      depthWrite: false,
    });
  }

  /**
   * 解析目标类型对应颜色。
   *
   * @param kind 命中目标类型。
   * @returns 颜色值。
   */
  private static resolveElementColor(kind: ViewOrientationTargetKind): THREE.ColorRepresentation {
    if (kind === ViewOrientationTargetKind.Edge) {
      return ViewOrientationMaterialFactory.EDGE_COLOR;
    }
    if (kind === ViewOrientationTargetKind.Corner) {
      return ViewOrientationMaterialFactory.CORNER_COLOR;
    }
    return ViewOrientationMaterialFactory.FACE_COLOR;
  }
}

